import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import backendService from '../services/backendService';
import useRouterMatch from './useRouterMatch';

export const classesCacheKey = 'classes';

function useClasses() {
  const queryClient = useQueryClient();
  // Scope the cache by tenant — requests are tenant-scoped via
  // getDestinationTenantFromUrl(), so the key must be too, or a workspace
  // switch could serve the previous tenant's classes within the stale window.
  const { tenantId } = useRouterMatch();

  const { data: classes, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [classesCacheKey, tenantId],
    queryFn: () => backendService.instance.classes.list(),
    staleTime: 1000 * 60,
  });

  const createMutation = useMutation({
    mutationFn: ({ name, studentNames }: { name: string; studentNames: string[] }) =>
      backendService.instance.classes.create({ name, studentNames }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [classesCacheKey, tenantId] });
    },
  });

  return {
    classes: classes ?? [],
    isLoading,
    isFetching,
    isError,
    refetch,
    createClass: createMutation.mutate,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
  };
}

export default useClasses;
